import {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';

import { captureCustomException } from '../ErrorTracking/captureCustomException';

const SCRIPT_ID = 'google-recaptcha-v3-embed-bootstrap';
const GRECAPTCHA_READY_POLL_MS = 15000;
const GRECAPTCHA_SCRIPT_WAIT_MS = 15000;

export const RECAPTCHA_V3_DEFAULT_ACTION = 'submit';

const RecaptchaV3Context = createContext(null);
const NOOP_RECAPTCHA_CONTEXT = {
    siteKey: null,
    tokenV3: null,
    setTokenV3: () => {},
    executeV3: async () => null,
};

/**
 * Reads the reCAPTCHA v3 state from the nearest `RecaptchaV3Provider`.
 *
 * @returns {{siteKey: string | null, tokenV3: string | null, setTokenV3: (token: string | null) => void, executeV3: (action?: string) => Promise<string | null>}} - Site key, last v3 token, its setter and a function that requests a fresh token; a no-op version when no provider is mounted.
 */
export function useRecaptchaV3() {
    return useContext(RecaptchaV3Context) ?? NOOP_RECAPTCHA_CONTEXT;
}

/**
 * Own reCAPTCHA v3 integration: loads api.js?render=siteKey, keeps tokenV3 for UI,
 * and exposes executeV3() for a fresh token before API calls (e.g. createLead).
 *
 * @param {object} props - Component props.
 * @param {string} props.siteKey - reCAPTCHA v3 site key; when omitted, tokens stay cleared and `executeV3` is a no-op.
 * @param {string} [props.language] - Optional `hl` query parameter on the injected `api.js` URL.
 * @param {import("react").ReactNode} props.children - Subtree that can call {@link useRecaptchaV3}.
 * @returns {import("react").JSX.Element} - The context provider wrapping `children`.
 */
export function RecaptchaV3Provider({ siteKey, language, children }) {
    const [tokenV3, setTokenV3] = useState(null);
    const tokenV3Ref = useRef(null);

    tokenV3Ref.current = tokenV3;

    const waitForExecuteApi = useCallback(async () => {
        if (!siteKey) return false;

        const deadline = Date.now() + GRECAPTCHA_SCRIPT_WAIT_MS;

        while ((!window.grecaptcha || !window.grecaptcha.execute) && Date.now() < deadline) {
            await new Promise((r) => requestAnimationFrame(r));
        }

        return Boolean(window.grecaptcha?.execute);
    }, [siteKey]);

    const executeV3 = useCallback(
        async (action = RECAPTCHA_V3_DEFAULT_ACTION) => {
            if (!siteKey) return null;

            const ready = await waitForExecuteApi();
            if (!ready) return tokenV3Ref.current;

            return new Promise((resolve) => {
                window.grecaptcha.ready(() => {
                    window.grecaptcha
                        .execute(siteKey, { action })
                        .then((t) => {
                            setTokenV3(t);
                            resolve(t);
                        })
                        .catch((err) => {
                            captureCustomException(err, 'reCAPTCHAv3Execute Failed');

                            resolve(tokenV3Ref.current);
                        });
                });
            });
        },
        [siteKey, waitForExecuteApi]
    );

    useEffect(() => {
        if (!siteKey) {
            setTokenV3(null);
            return undefined;
        }

        let cancelled = false;
        let rafId = null;

        const cleanup = () => {
            cancelled = true;

            if (rafId !== null) {
                cancelAnimationFrame(rafId);
                rafId = null;
            }
        };

        const runExecute = () => {
            if (!window.grecaptcha?.ready) return;

            window.grecaptcha.ready(() => {
                if (cancelled || !window.grecaptcha?.execute) return;

                window.grecaptcha
                    .execute(siteKey, { action: RECAPTCHA_V3_DEFAULT_ACTION })
                    .then((t) => {
                        setTokenV3(t);
                    })
                    .catch((err) => {
                        captureCustomException(err, 'reCAPTCHAv3RunExecute Failed');
                    });
            });
        };

        const waitForApiThenExecute = () => {
            const started = Date.now();

            const tick = () => {
                if (cancelled) return;

                if (window.grecaptcha?.ready) {
                    runExecute();
                    return;
                }

                if (Date.now() - started < GRECAPTCHA_READY_POLL_MS) {
                    rafId = requestAnimationFrame(tick);
                }
            };

            tick();
        };

        const existing = document.getElementById(SCRIPT_ID);
        if (existing) {
            waitForApiThenExecute();
            return cleanup;
        }

        const s = document.createElement('script');
        s.id = SCRIPT_ID;
        s.async = true;
        s.defer = true;
        const hl = language ? `&hl=${encodeURIComponent(language)}` : '';
        s.src = `https://www.google.com/recaptcha/api.js?render=${encodeURIComponent(siteKey)}${hl}`;
        s.onload = () => waitForApiThenExecute();
        s.onerror = () => {
            captureCustomException(
                new Error('Google reCAPTCHA v3 script failed to load'),
                'reCAPTCHAv3ScriptLoad Failed'
            );
        };
        document.head.appendChild(s);

        return cleanup;
    }, [siteKey, language]);

    const value = useMemo(
        () => ({
            siteKey,
            tokenV3,
            setTokenV3,
            executeV3,
        }),
        [siteKey, tokenV3, executeV3]
    );

    return <RecaptchaV3Context value={value}>{children}</RecaptchaV3Context>;
}

export default RecaptchaV3Provider;
